import type { PortalProviderAPI } from '@atlaskit/editor-common/portal';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import {
	type EditorState,
	PluginKey,
	type ReadonlyTransaction,
} from '@atlaskit/editor-prosemirror/state';
import { DecorationSet, type EditorView } from '@atlaskit/editor-prosemirror/view';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

import type { HubExternalInsertPlugin } from '../hubExternalInsertPluginType';

import {
	createCursorIndicatorDecoration,
	findCursorIndicatorDecs,
} from './create-cursor-indicator-decoration';

const HUB_EXTENSION_DRAG_TYPE = 'hub-extension';

export const key: PluginKey<PluginState> = new PluginKey<PluginState>('hubExternalInsert');

export interface PluginState {
	decorations: DecorationSet;
	showCursorDropIndicator: boolean;
}

const initialState: PluginState = {
	decorations: DecorationSet.empty,
	showCursorDropIndicator: false,
};

export function createPlugin(
	api: ExtractInjectionAPI<HubExternalInsertPlugin> | undefined,
	nodeViewPortalProviderAPI: PortalProviderAPI | undefined,
): SafePlugin<PluginState> {
	// If portal API is missing (e.g. wrong editor wiring), run as no-op so the page doesn't break
	if (!nodeViewPortalProviderAPI || typeof nodeViewPortalProviderAPI.render !== 'function') {
		return new SafePlugin({
			key,
			state: {
				init: () => initialState,
				apply: (_tr, state) => state,
			},
			props: {
				decorations: (state) => key.getState(state)?.decorations,
			},
		});
	}

	return new SafePlugin({
		key,
		state: {
			init: () => initialState,
			apply: (
				tr: ReadonlyTransaction,
				currentState: PluginState,
				_oldState: EditorState,
				newState: EditorState,
			) => {
				try {
					let decorations = currentState.decorations;
					const meta = tr.getMeta(key) as { showCursorDropIndicator?: boolean } | undefined;
					const showCursorDropIndicator =
						meta?.showCursorDropIndicator ?? currentState.showCursorDropIndicator;

					if (tr.docChanged) {
						decorations = decorations.map(tr.mapping, tr.doc);
					}

					const cursorDecs = findCursorIndicatorDecs(decorations);
					decorations = decorations.remove(cursorDecs);

					if (showCursorDropIndicator) {
						const $from = newState.selection.$from;
						const pos = $from.before();
						const prevNode = $from.nodeBefore;
						const nextNode = $from.nodeAfter;
						const parentNode = $from.node();
						const lineLength = api?.width?.sharedState?.currentState()?.lineLength;
						const dec = createCursorIndicatorDecoration(
							pos,
							nodeViewPortalProviderAPI,
							prevNode,
							nextNode,
							parentNode,
							lineLength,
						);
						decorations = decorations.add(newState.doc, [dec]);
					}

					return {
						decorations,
						showCursorDropIndicator,
					};
				} catch {
					return currentState;
				}
			},
		},
		props: {
			decorations: (state) => key.getState(state)?.decorations,
		},
		view(editorView: EditorView) {
			let cleanup: (() => void) | undefined;
			try {
				cleanup = monitorForElements({
					canMonitor: ({ source }) => source.data.type === HUB_EXTENSION_DRAG_TYPE,
					onDragStart: () => {
						editorView.dispatch(
							editorView.state.tr.setMeta(key, { showCursorDropIndicator: true }),
						);
					},
					onDrop: () => {
						editorView.dispatch(
							editorView.state.tr.setMeta(key, { showCursorDropIndicator: false }),
						);
					},
				});
			} catch {}
			return {
				destroy() {
					try {
						cleanup?.();
					} catch {}
				},
			};
		},
	});
}
